import React from 'react';
import { Field, Section, StringList } from './Fields';
import ImageUploader from './ImageUploader';

const LANG_CODES = ['fr', 'en', 'es', 'de', 'it'];

const GlobalEditor = ({ config, setConfig, token }) => {
  const patch = (path, value) => {
    setConfig((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      let ref = next;
      for (let i = 0; i < path.length - 1; i++) {
        if (ref[path[i]] === undefined) ref[path[i]] = {};
        ref = ref[path[i]];
      }
      ref[path[path.length - 1]] = value;
      return next;
    });
  };

  const addContact = () => {
    setConfig((prev) => ({
      ...prev,
      contacts: [
        ...(prev.contacts || []),
        { id: `c_${Date.now()}`, photo: '', name: 'Nouveau contact', whatsapp: '', phone: '', email: '', showInLangs: ['fr'], roles: { fr: '', en: '', es: '', de: '' } },
      ],
    }));
  };

  const removeContact = (idx) => {
    setConfig((prev) => ({ ...prev, contacts: prev.contacts.filter((_, i) => i !== idx) }));
  };

  const addCircuit = () => {
    const id = `c_${Date.now()}`;
    setConfig((prev) => ({
      ...prev,
      circuits: [
        ...(prev.circuits || []),
        {
          id, color: 'nord', image: '', price: 50, duration: '4h', start: '09:00', end: '13:00',
          availableLangs: ['fr'],
          translations: {
            fr: { badge: '', title: 'Nouveau circuit', subtitle: '', stops: [], description: '' },
            en: { badge: '', title: '', subtitle: '', stops: [], description: '' },
            es: { badge: '', title: '', subtitle: '', stops: [], description: '' },
            de: { badge: '', title: '', subtitle: '', stops: [], description: '' },
          },
        },
      ],
    }));
  };

  const removeCircuit = (idx) => {
    if (!window.confirm('Supprimer définitivement ce circuit ?')) return;
    setConfig((prev) => ({ ...prev, circuits: prev.circuits.filter((_, i) => i !== idx) }));
  };

  return (
    <div className="space-y-6">
      <Section title="Logo du site" description="Affiché dans l'entête et le pied de page. Image ronde recommandée (carrée, ≥ 200×200 px).">
        <div className="max-w-[220px]">
          <ImageUploader
            label=""
            value={config?.branding?.logo}
            onChange={(v) => patch(['branding', 'logo'], v)}
            token={token}
            aspect="aspect-square"
          />
        </div>
      </Section>

      <Section title="Circuits (données communes)" description="Prix, durée, horaires, couleur et image sont communs à toutes les langues. Éditez les textes par langue dans les onglets FR/EN/ES/DE/IT.">
        <div className="space-y-4">
          {(config.circuits || []).map((c, ci) => (
            <div key={c.id} className="rounded-lg border border-white/10 bg-black/20 p-5">
              <div className="flex items-center justify-between gap-4">
                <div className="font-serif text-lg text-white">
                  {c.translations?.fr?.title || c.id}
                  <span className="ml-2 text-[11px] text-white/40">({c.id})</span>
                </div>
                <button type="button" onClick={() => removeCircuit(ci)} className="text-[11px] text-white/40 hover:text-[#f27a5b]">Supprimer</button>
              </div>

              <div className="mt-4 grid grid-cols-1 md:grid-cols-[240px_1fr] gap-5">
                <ImageUploader label="Photo" value={c.image} onChange={(v) => patch(['circuits', ci, 'image'], v)} token={token} />

                <div className="space-y-3">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    <Field label="Identifiant" value={c.id} onChange={(v) => patch(['circuits', ci, 'id'], v)} />
                    <div>
                      <label className="block text-[11px] tracking-[0.15em] uppercase text-white/50 mb-1.5">Couleur</label>
                      <select
                        value={c.color}
                        onChange={(e) => patch(['circuits', ci, 'color'], e.target.value)}
                        className="w-full rounded-md bg-[#0a1f1a] border border-white/10 focus:border-[#e0b972] focus:outline-none px-3 py-2 text-[13.5px] text-white"
                      >
                        <option value="nord">Vert (Nord)</option>
                        <option value="sud">Turquoise (Sud)</option>
                        <option value="plage">Orange (Plage)</option>
                        <option value="banana">Jaune (Banana)</option>
                        <option value="transfert">Bleu marine (Transfert)</option>
                      </select>
                    </div>
                    <Field label="Prix (€)" type="number" value={c.price} onChange={(v) => patch(['circuits', ci, 'price'], v)} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <Field label="Durée" value={c.duration} onChange={(v) => patch(['circuits', ci, 'duration'], v)} placeholder="5h" />
                    <Field label="Début" value={c.start} onChange={(v) => patch(['circuits', ci, 'start'], v)} placeholder="08:30 ou —" />
                    <Field label="Fin" value={c.end} onChange={(v) => patch(['circuits', ci, 'end'], v)} placeholder="13:30 ou —" />
                  </div>
                  <div>
                    <label className="block text-[11px] tracking-[0.15em] uppercase text-white/50 mb-1.5">Langues disponibles</label>
                    <div className="flex gap-2">
                      {LANG_CODES.map((l) => {
                        const enabled = (c.availableLangs || []).includes(l);
                        return (
                          <button
                            key={l}
                            type="button"
                            onClick={() => {
                              const cur = c.availableLangs || [];
                              const next = cur.includes(l) ? cur.filter((x) => x !== l) : [...cur, l];
                              patch(['circuits', ci, 'availableLangs'], next);
                            }}
                            className={`px-3 py-1.5 text-[12px] rounded-md border transition-colors ${
                              enabled ? 'bg-[#e0b972] text-[#0e2a24] border-[#e0b972]' : 'text-white/60 border-white/15 hover:border-white/30'
                            }`}
                          >
                            {l.toUpperCase()}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <button type="button" onClick={addCircuit} className="w-full rounded-md border border-dashed border-white/15 hover:border-[#e0b972] py-3 text-[13px] text-white/60 hover:text-[#e0b972] transition-colors">
            + Ajouter un circuit
          </button>
        </div>
      </Section>

      <Section title="Contacts affichés" description="Ajoutez vos contacts avec photos. Cochez les langues dans lesquelles chaque contact apparaitra.">
        <div className="space-y-4">
          {(config.contacts || []).map((c, ci) => (
            <div key={c.id} className="rounded-lg border border-white/10 bg-black/20 p-5">
              <div className="flex items-center justify-between">
                <div className="font-serif text-lg text-white">{c.name}</div>
                <button type="button" onClick={() => removeContact(ci)} className="text-[11px] text-white/40 hover:text-[#f27a5b]">Supprimer</button>
              </div>
              <div className="mt-4 grid grid-cols-1 md:grid-cols-[180px_1fr] gap-5">
                <ImageUploader label="Photo" value={c.photo} onChange={(v) => patch(['contacts', ci, 'photo'], v)} token={token} aspect="aspect-square" />
                <div className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="Nom" value={c.name} onChange={(v) => patch(['contacts', ci, 'name'], v)} />
                    <Field label="Email" value={c.email} onChange={(v) => patch(['contacts', ci, 'email'], v)} />
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <Field label="WhatsApp (numéro sans +)" value={c.whatsapp} onChange={(v) => patch(['contacts', ci, 'whatsapp'], v)} placeholder="596696396739" />
                    <Field label="Téléphone (affiché)" value={c.phone} onChange={(v) => patch(['contacts', ci, 'phone'], v)} placeholder="+596 696 39 67 39" />
                  </div>
                  <div>
                    <label className="block text-[11px] tracking-[0.15em] uppercase text-white/50 mb-1.5">Affiché sur les pages</label>
                    <div className="flex gap-2">
                      {LANG_CODES.map((l) => {
                        const enabled = (c.showInLangs || []).includes(l);
                        return (
                          <button
                            key={l}
                            type="button"
                            onClick={() => {
                              const cur = c.showInLangs || [];
                              const next = cur.includes(l) ? cur.filter((x) => x !== l) : [...cur, l];
                              patch(['contacts', ci, 'showInLangs'], next);
                            }}
                            className={`px-3 py-1.5 text-[12px] rounded-md border transition-colors ${
                              enabled ? 'bg-[#e0b972] text-[#0e2a24] border-[#e0b972]' : 'text-white/60 border-white/15 hover:border-white/30'
                            }`}
                          >
                            {l.toUpperCase()}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
          <button type="button" onClick={addContact} className="w-full rounded-md border border-dashed border-white/15 hover:border-[#e0b972] py-3 text-[13px] text-white/60 hover:text-[#e0b972] transition-colors">
            + Ajouter un contact
          </button>
        </div>
      </Section>
    </div>
  );
};

export default GlobalEditor;
